/**
 * <pack-selector>
 *
 * Reemplaza visualmente al selector de variantes del tema para una sola
 * opcion (por defecto "Cantidad") con pildoras + panel de resumen.
 *
 * No sustituye la logica del tema: al elegir una pildora escribe el valor
 * en el control nativo (<select> o radios) y dispara un evento 'change',
 * de modo que el tema siga encargandose del precio, la imagen y el boton
 * de compra. Si el JS falla, el selector nativo queda visible y usable.
 */
// Los atributos de presentacion van en el propio SVG para que los iconos se
// vean correctos aunque la hoja de estilos no haya cargado todavia.
const PACK_SVG_ATTRS =
  'class="pack-chip__icon" viewBox="0 0 24 24" width="18" height="18" ' +
  'fill="none" stroke="currentColor" stroke-width="1.6" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"';

const PACK_ICONS = {
  // Caja cerrada: representa el total de piezas que recibe
  box:
    '<svg ' + PACK_SVG_ATTRS + '>' +
    '<path d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5v-9Z"/>' +
    '<path d="m3 7.5 9 4.5 9-4.5M12 12v9"/>' +
    '<path d="M7.5 5.25 16.5 9.75"/>' +
    '</svg>',
  // Paquetes apilados: representa el desglose por paquete
  stack:
    '<svg ' + PACK_SVG_ATTRS + '>' +
    '<path d="M12 2.5 21 7l-9 4.5L3 7l9-4.5Z"/>' +
    '<path d="m3 12 9 4.5L21 12"/>' +
    '<path d="m3 16.75 9 4.5 9-4.5"/>' +
    '</svg>',
};

class PackSelector extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-pack-data]');
    if (!dataEl) return;

    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    this.optionIndex = this.data.optionIndex || 0;
    this.optionName = this.data.optionName || 'Cantidad';
    this.changesPerDay = parseInt(this.dataset.changesPerDay, 10) || 6;
    this.unitLabel = this.dataset.unitLabel || 'pieza';
    this.unitLabelPlural = this.dataset.unitLabelPlural || 'piezas';
    this.showDuration = this.dataset.showDuration === 'true';
    this.showSavings = this.dataset.showSavings !== 'false';
    this.hideNative = this.dataset.hideNative !== 'false';

    this.showSavingsChip = this.dataset.showSavingsChip !== 'false';
    this.showUnitChip = this.dataset.showUnitChip !== 'false';
    this.heroTitleTpl = this.dataset.heroTitle || 'Caja con {pieces} {unit_plural}';
    this.heroSubtitleTpl = this.dataset.heroSubtitle || '';
    this.savingsLabelTpl = this.dataset.savingsLabel || 'Ahorras {amount}';
    this.unitChipTpl = this.dataset.unitChipLabel || 'Solo {price} por {unit}';
    this.fallbackPieces = parseInt(this.dataset.fallbackPieces, 10) || 0;
    this.fallbackPacks = parseInt(this.dataset.fallbackPacks, 10) || 0;

    this.pills = Array.from(this.querySelectorAll('[data-pack-pill]'));
    this.summaryMain = this.querySelector('[data-pack-summary-main]');
    this.summaryMeta = this.querySelector('[data-pack-summary-meta]');
    this.summarySave = this.querySelector('[data-pack-summary-save]');

    this.hero = this.querySelector('[data-pack-hero]');
    this.heroTitle = this.querySelector('[data-pack-hero-title]');
    this.heroSub = this.querySelector('[data-pack-hero-sub]');

    this.priceRow = this.querySelector('[data-pack-price]');
    this.priceNow = this.querySelector('[data-pack-price-now]');
    this.priceWas = this.querySelector('[data-pack-price-was]');
    this.priceSave = this.querySelector('[data-pack-price-save]');
    this.priceSaveText = this.querySelector('[data-pack-price-save-text]');
    this.priceUnit = this.querySelector('[data-pack-price-unit]');

    this.money = this.buildMoneyFormatter();

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onExternalChange = this.onExternalChange.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('change', this.onExternalChange);

    this.hideNativePicker();

    // Sin pildoras, ni panel, ni fila de precio el bloque solo sirve para
    // ocultar el selector nativo: se colapsa su contenedor para no dejar el
    // margen vertical como hueco en blanco.
    if (this.classList.contains('pack-selector--hidden')) {
      const wrapper = this.closest('.mt-product-global');
      if (wrapper) wrapper.style.display = 'none';
      return;
    }

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onExternalChange);
  }

  /* ---------- Formato de moneda ---------- */

  /**
   * Replica el formato de moneda de la tienda (shop.money_format) para que los
   * importes que pinta el JS sean identicos a los que pinta Liquid.
   * `forceDecimals` se usa en el precio por pieza: sin decimales quedaria "$4".
   */
  buildMoneyFormatter() {
    const fmt = this.dataset.moneyFormat || '${{amount}}';
    const noDecimals = fmt.indexOf('no_decimals') !== -1;
    const commaDecimal = fmt.indexOf('comma_separator') !== -1;
    const spaceThousands = fmt.indexOf('space_separator') !== -1;
    const apostropheThousands = fmt.indexOf('apostrophe_separator') !== -1;

    const prefix = fmt.split('{{')[0] || '';
    const chunks = fmt.split('}}');
    const suffix = chunks.length > 1 ? chunks[chunks.length - 1] : '';

    let thousands = ',';
    if (spaceThousands) thousands = ' ';
    else if (apostropheThousands) thousands = "'";
    else if (commaDecimal) thousands = '.';

    const decimalMark = commaDecimal ? ',' : '.';

    return (cents, forceDecimals) => {
      const digits = forceDecimals || !noDecimals ? 2 : 0;
      const parts = (Math.round(cents) / 100).toFixed(digits).split('.');
      const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const body = parts[1] ? int + decimalMark + parts[1] : int;
      return prefix + body + suffix;
    };
  }

  /* ---------- Acceso al selector nativo del tema ---------- */

  get scope() {
    return this.closest('.shopify-section, [data-section-id], section') || document;
  }

  get nativeRoot() {
    return this.scope.querySelector('variant-selects, variant-radios');
  }

  findControl() {
    const root = this.nativeRoot;
    if (!root) return null;

    const select = root.querySelector('select[name="options[' + this.optionName + ']"]');
    if (select) return { type: 'select', el: select };

    const radios = root.querySelectorAll('input[type="radio"][name="' + this.optionName + '"]');
    if (radios.length) return { type: 'radio', els: Array.from(radios) };

    // Sin coincidencia por nombre: cae al control que ocupe la misma posicion.
    const selects = root.querySelectorAll('select');
    if (selects[this.optionIndex]) return { type: 'select', el: selects[this.optionIndex] };

    const fieldsets = root.querySelectorAll('fieldset');
    if (fieldsets[this.optionIndex]) {
      const els = Array.from(fieldsets[this.optionIndex].querySelectorAll('input[type="radio"]'));
      if (els.length) return { type: 'radio', els: els };
    }

    return null;
  }

  hideNativePicker() {
    if (!this.hideNative) return;

    const control = this.findControl();
    if (!control) return;

    const el = control.type === 'select' ? control.el : control.els[0];
    const wrapper = el.closest('.form-group, .prd-option');
    if (wrapper) wrapper.classList.add('pack-selector__native-hidden');
  }

  /** Valores actualmente seleccionados, en el orden de las opciones del producto. */
  readOptions() {
    const root = this.nativeRoot;
    if (!root) return null;

    const selects = root.querySelectorAll('select');
    if (selects.length) return Array.from(selects).map((s) => s.value);

    const fieldsets = root.querySelectorAll('fieldset');
    if (fieldsets.length) {
      return Array.from(fieldsets).map((fs) => {
        const checked = fs.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });
    }

    return null;
  }

  /* ---------- Resolucion de variantes ---------- */

  variantFor(value) {
    const current = this.readOptions();

    if (current) {
      const match = this.data.variants.find((v) =>
        v.options.every((o, i) => (i === this.optionIndex ? o === value : o === current[i]))
      );
      if (match) return match;
    }

    return this.data.variants.find((v) => v.options[this.optionIndex] === value) || null;
  }

  get selectedValue() {
    const active = this.pills.find((p) => p.classList.contains('is-active'));
    return active ? active.dataset.value : null;
  }

  /* ---------- Eventos ---------- */

  onClick(event) {
    const pill = event.target.closest('[data-pack-pill]');
    if (!pill || pill.disabled) return;

    event.preventDefault();
    this.select(pill.dataset.value);
  }

  onKeydown(event) {
    if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].indexOf(event.key) === -1) return;

    const enabled = this.pills.filter((p) => !p.disabled);
    if (!enabled.length) return;

    const currentIndex = enabled.findIndex((p) => p.classList.contains('is-active'));
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const next = enabled[(currentIndex + step + enabled.length) % enabled.length];

    event.preventDefault();
    this.select(next.dataset.value);
    next.focus();
  }

  /** Cambios disparados por el tema o por otra opcion del producto. */
  onExternalChange(event) {
    const target = event.target;
    if (!target || target === this) return;
    if (this.contains(target)) return;
    if (!target.matches('select, input[type="radio"]')) return;
    if (!this.nativeRoot || !this.nativeRoot.contains(target)) return;

    this.render();
  }

  /* ---------- Seleccion ---------- */

  select(value) {
    const control = this.findControl();

    if (control) {
      if (control.type === 'select') {
        control.el.value = value;
        control.el.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        const radio = control.els.find((r) => r.value === value);
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    this.setActive(value);
    this.render(value);
  }

  setActive(value) {
    this.pills.forEach((pill) => {
      const active = pill.dataset.value === value;
      pill.classList.toggle('is-active', active);
      pill.setAttribute('aria-checked', active ? 'true' : 'false');
    });
  }

  /* ---------- Render ---------- */

  render(forcedValue) {
    const current = this.readOptions();
    const value = forcedValue || (current ? current[this.optionIndex] : null) || this.selectedValue;
    if (!value) return;

    this.setActive(value);
    this.hideNativePicker();
    this.renderPills();
    this.renderHero(value);
    this.renderPriceRow(value);
    this.renderSummary(value);
  }

  /* ---------- Plantillas de texto ---------- */

  /**
   * Sustituye los marcadores de una plantilla del editor de temas.
   * La plantilla se escapa antes de sustituir, asi que solo entra el HTML que
   * genera este archivo ({pieces} y {amount} van en <strong>).
   */
  fill(template, tokens) {
    let out = this.escape(template);

    Object.keys(tokens).forEach((key) => {
      out = out.split('{' + key + '}').join(tokens[key]);
    });

    return out;
  }

  /** Piezas y paquetes de una variante, con el respaldo calculado en Liquid. */
  countsFor(variant) {
    const pieces = (variant && variant.pieces) || this.fallbackPieces || 0;
    let packs = (variant && variant.packs) || 0;

    if (!packs && pieces > 0 && this.unitsPerPack > 0) {
      packs = Math.round(pieces / this.unitsPerPack);
    }

    return { pieces: pieces, packs: packs };
  }

  get unitsPerPack() {
    return this.data.unitsPerPack || parseInt(this.dataset.unitsPerPack, 10) || 0;
  }

  /* ---------- Panel destacado (hero) ---------- */

  renderHero(value) {
    if (!this.hero || !this.heroTitle) return;

    const variant = this.variantFor(value);
    const counts = this.countsFor(variant);

    if (!counts.pieces) {
      this.hero.hidden = true;
      return;
    }

    const perPack = counts.packs > 0 ? Math.round(counts.pieces / counts.packs) : 0;
    const noun = counts.pieces === 1 ? this.unitLabel : this.unitLabelPlural;

    const tokens = {
      pieces: '<strong>' + counts.pieces + '</strong>',
      packs: counts.packs,
      per_pack: perPack,
      unit: this.escape(this.unitLabel),
      unit_plural: this.escape(this.unitLabelPlural),
      noun: this.escape(noun),
    };

    this.hero.hidden = false;
    this.heroTitle.innerHTML = this.fill(this.heroTitleTpl, tokens);

    if (this.heroSub) {
      const needsPacks =
        this.heroSubtitleTpl.indexOf('{packs}') !== -1 ||
        this.heroSubtitleTpl.indexOf('{per_pack}') !== -1;

      if (!this.heroSubtitleTpl || (needsPacks && counts.packs < 2)) {
        this.heroSub.hidden = true;
      } else {
        // En el subtitulo las piezas van sin <strong>: el numero destacado es
        // el del titulo.
        this.heroSub.innerHTML = this.fill(
          this.heroSubtitleTpl,
          Object.assign({}, tokens, { pieces: counts.pieces })
        );
        this.heroSub.hidden = false;
      }
    }
  }

  /* ---------- Fila de precio ---------- */

  renderPriceRow(value) {
    if (!this.priceRow) return;

    const variant = this.variantFor(value);
    if (!variant) return;

    const counts = this.countsFor(variant);
    const compare = variant.compareAtPrice || 0;
    const hasCompare = compare > variant.price;

    if (this.priceNow) this.priceNow.textContent = this.money(variant.price);

    if (this.priceWas) {
      this.priceWas.textContent = hasCompare ? this.money(compare) : '';
      this.priceWas.hidden = !hasCompare;
    }

    if (this.priceSave) {
      if (hasCompare && this.showSavingsChip) {
        const amount = compare - variant.price;

        if (this.priceSaveText) {
          this.priceSaveText.innerHTML = this.chipParts(
            this.savingsLabelTpl,
            '{amount}',
            this.money(amount),
            { percent: Math.round((amount * 100) / compare) }
          );
        }

        this.priceSave.hidden = false;
      } else {
        this.priceSave.hidden = true;
      }
    }

    if (this.priceUnit) {
      if (counts.pieces > 0 && this.showUnitChip) {
        this.priceUnit.innerHTML = this.chipParts(
          this.unitChipTpl,
          '{price}',
          this.money(variant.price / counts.pieces, true)
        );
        this.priceUnit.hidden = false;
      } else {
        this.priceUnit.hidden = true;
      }
    }
  }

  /**
   * Parte una plantilla en "texto pequeno / importe destacado / texto pequeno"
   * alrededor de su marcador de importe. Es la anatomia que comparten las dos
   * etiquetas para poder apilarlas en movil.
   */
  chipParts(template, token, money, extraTokens) {
    const tokens = Object.assign(
      {
        unit: this.escape(this.unitLabel),
        unit_plural: this.escape(this.unitLabelPlural),
      },
      extraTokens || {}
    );

    const pieces = template.split(token);
    const before = this.fill(pieces[0] || '', tokens).trim();
    const after = this.fill(pieces[1] || '', tokens).trim();

    return (
      (before ? '<span class="pack-price__chip-label">' + before + '</span>' : '') +
      '<strong>' + this.escape(money) + '</strong>' +
      (after ? '<span class="pack-price__chip-label">' + after + '</span>' : '')
    );
  }

  /** Recalcula el precio por pieza de cada pildora segun las otras opciones. */
  renderPills() {
    this.pills.forEach((pill) => {
      const variant = this.variantFor(pill.dataset.value);
      if (!variant) return;

      const pieces = variant.pieces || parseInt(pill.dataset.pieces, 10) || 0;
      const unitEl = pill.querySelector('[data-pack-unit]');
      const saveEl = pill.querySelector('[data-pack-save]');

      if (unitEl) {
        unitEl.textContent = pieces > 0
          ? this.money(variant.price / pieces) + ' c/u'
          : this.money(variant.price);
      }

      if (saveEl) {
        const compare = variant.compareAtPrice || 0;
        saveEl.textContent = compare > variant.price
          ? '-' + Math.round(((compare - variant.price) * 100) / compare) + '%'
          : '';
      }

      pill.classList.toggle('is-soldout', !variant.available);
      pill.disabled = !variant.available;
    });
  }

  /** Construye un chip con icono para el panel de resumen. */
  chip(icon, label, isPrimary) {
    return (
      '<span class="pack-chip' + (isPrimary ? ' pack-chip--primary' : '') + '">' +
      icon +
      '<span class="pack-chip__text">' + label + '</span>' +
      '</span>'
    );
  }

  escape(text) {
    const div = document.createElement('div');
    div.textContent = text == null ? '' : String(text);
    return div.innerHTML;
  }

  renderSummary(value) {
    const variant = this.variantFor(value);
    if (!variant || !this.summaryMain) return;

    const pieces = variant.pieces || 0;
    const packs = variant.packs || 0;
    const noun = pieces === 1 ? this.unitLabel : this.unitLabelPlural;

    // Linea principal: chips con el contenido exacto del pack
    const chips = [];

    if (pieces > 0) {
      chips.push(this.chip(PACK_ICONS.box, '<strong>' + pieces + '</strong> ' + noun, true));

      if (packs > 1) {
        const perPack = Math.round(pieces / packs);
        chips.push(
          this.chip(PACK_ICONS.stack, '<strong>' + packs + '</strong> paquetes de ' + perPack + ' c/u')
        );
      } else if (packs === 1) {
        chips.push(this.chip(PACK_ICONS.stack, '<strong>1</strong> paquete'));
      }
    } else {
      chips.push(this.chip(PACK_ICONS.box, this.escape(value), true));
    }

    this.summaryMain.innerHTML = chips.join('');

    // Linea secundaria: precio por pieza y duracion estimada
    if (this.summaryMeta) {
      const parts = [];

      if (pieces > 0) {
        parts.push('<strong>' + this.money(variant.price / pieces) + '</strong> por ' + this.unitLabel);

        if (this.showDuration && this.changesPerDay > 0) {
          const days = Math.round(pieces / this.changesPerDay);
          if (days >= 60) {
            parts.push('te dura ~' + Math.round(days / 30) + ' meses');
          } else if (days >= 14) {
            parts.push('te dura ~' + Math.round(days / 7) + ' semanas');
          } else if (days > 0) {
            parts.push('te dura ~' + days + ' días');
          }
        }
      }

      this.summaryMeta.innerHTML = parts.join(' · ');
      this.summaryMeta.hidden = parts.length === 0;
    }

    // Linea de ahorro
    if (this.summarySave) {
      const compare = this.showSavings ? variant.compareAtPrice || 0 : 0;
      if (compare > variant.price) {
        const amount = compare - variant.price;
        const percent = Math.round((amount * 100) / compare);
        this.summarySave.textContent = 'Ahorras ' + this.money(amount) + ' (' + percent + '%)';
        this.summarySave.hidden = false;
      } else {
        this.summarySave.hidden = true;
      }
    }
  }
}

if (!customElements.get('pack-selector')) {
  customElements.define('pack-selector', PackSelector);
}
